import React from 'react';
import styled from '@emotion/styled';
import { useLocation } from 'react-router';
import { spacing } from '@naf/theme';
import { Button } from '@naf-simba/elements-buttons';
import { mapSimbaVariant } from '@utils/simba/mapSimbaVariant';
import { ColorVariant } from '../../../../../../../types/featuredBenefitBlockType';
import { useDocumentUrlWithNode } from '../../../../../hooks/Documents/useDocumentUrl';
import { VippsLoginButton } from '../../../../VippsLoginButton';
import { LoginButtonDefinition } from '../../../../../../../types/heroes/heroWithTitleImageBodyAndLoginType';
import { useLoginRedirect } from '../../../../../hooks/useLoginRedirect';
import { useTrackCTA } from '../../../../../utils/Tracking/CtaClick/TrackCtaClick';

const LoginButtonsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.space16};
`;

export const LoginButtons = ({ login }: { login: LoginButtonDefinition[] }) => (
  <LoginButtonsWrapper>
    {login.map((definition) => (
      <LoginButtonMapper definition={definition} key={definition.key} />
    ))}
  </LoginButtonsWrapper>
);

const LoginButtonMapper = ({ definition }: { definition: LoginButtonDefinition }) => {
  const location = useLocation();
  const { returnTo, buttonText, colorVariant } = definition;
  const { linkType, ctaInternalLink, ctaExternalLink } = returnTo;
  const ctaUrl = useDocumentUrlWithNode(ctaInternalLink?.referenceComponent);

  const getReturnUrl = () => {
    const currentUrl = `${location.pathname}${location.search}`;
    switch (linkType) {
      case '':
        return currentUrl;
      case 'internalLink':
        return ctaUrl || currentUrl;
      case 'externalLink':
        return ctaExternalLink?.link || currentUrl;
      default:
        return currentUrl;
    }
  };

  switch (definition.type) {
    case 'vippsLoginButton':
      return <VippsLoginButton returnUrl={getReturnUrl()} isStretched />;
    case 'defaultLoginButton':
      return (
        <DefaultLoginButton
          returnUrl={getReturnUrl()}
          buttonText={buttonText}
          colorVariant={colorVariant}
          key={definition.key}
        />
      );
    default:
      return null;
  }
};

const DefaultLoginButton = ({
  returnUrl,
  buttonText,
  colorVariant,
}: {
  returnUrl: string;
  buttonText?: string;
  colorVariant?: ColorVariant;
}) => {
  const trackCTA = useTrackCTA();
  const { redirectToLoginPage } = useLoginRedirect();
  const buttonVariant = mapSimbaVariant(colorVariant);

  const handleTracking = () => {
    trackCTA({
      cta_position: 'loginHero',
      text: 'Logg inn',
    });
  };

  return (
    <Button
      variant={buttonVariant.variant}
      color={buttonVariant.color}
      shade={buttonVariant.shade}
      onClick={() => {
        handleTracking();
        redirectToLoginPage(returnUrl);
      }}
    >
      {buttonText ?? 'Logg inn'}
    </Button>
  );
};
